// Panel de "Mejorar calidad" (upscaling de video con Topaz vía Replicate).
//
// Corre el flujo async de `upscaleVideo` (START + poll STATUS). El componente NO
// persiste: al terminar llama `onSave(result)` y el contenedor decide qué hacer
// (registrar el asset + reemplazar el clip). Se abre en la sidebar del editor,
// igual que EditarVideoIA. Ver ADR-596.
import React, { useRef, useState } from 'react';
import { Loader2, Check, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { upscaleVideo, type UpscaleResolution } from '../lib/videoUpscale';
import { creditsForVideoUpscale } from '../utils/credits';
import { CreditBadge } from './CreditBadge';

export interface UpscaleSaveResult {
  /** Signed URL del video mejorado. */
  url: string;
  /** Storage path del video mejorado en `video-project-assets`. */
  path: string;
  /** Lo que mide el archivo nuevo (el tramo). Ausente con un backend viejo. */
  segundos?: number;
}

export interface UpscaleVideoPanelProps {
  companyId: string;
  projectId: string;
  clipId: string;
  /** Signed URL (descargable) del clip a mejorar. Puede estar por vencer. */
  clipUrl: string;
  /** Storage path del clip, para re-firmar antes de disparar (el upscale tarda
   *  minutos y `clipUrl` puede haber expirado si el proyecto estuvo abierto ~1h). */
  clipStoragePath?: string;
  /** Segundos de material del tramo del clip (lo que se mejora y se cobra). */
  durationSec: number;
  /** Dónde empieza el tramo en el archivo: el backend mejora sólo ese tramo. */
  mediaOffsetSec?: number;
  /** Se llama al terminar; el contenedor registra el asset y reemplaza el clip. */
  onSave: (result: UpscaleSaveResult) => void | Promise<void>;
  /** El backend aceptó el trabajo: el contenedor guarda el id para retomarlo. */
  onIniciado?: (predictionId: string) => void;
  onClose: () => void;
}

const RESOLUTIONS: { id: UpscaleResolution; label: string }[] = [
  { id: '720p', label: '720p' },
  { id: '1080p', label: '1080p' },
  { id: '4k', label: '4K' },
];

export const UpscaleVideoPanel: React.FC<UpscaleVideoPanelProps> = ({
  companyId,
  projectId,
  clipId,
  clipUrl,
  clipStoragePath,
  durationSec,
  mediaOffsetSec,
  onSave,
  onIniciado,
  onClose,
}) => {
  const [resolution, setResolution] = useState<UpscaleResolution>('1080p');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const savingRef = useRef(false);

  const cost = creditsForVideoUpscale({
    durationSec,
    targetResolution: resolution,
  });
  const resLabel =
    RESOLUTIONS.find((r) => r.id === resolution)?.label ?? resolution;

  const handleUpscale = async () => {
    if (busy || savingRef.current) return;
    setBusy(true);
    setError(null);
    setProgress('Iniciando…');
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('Sesión no encontrada. Recargá la página.');

      // Re-firmar la URL antes de disparar: el upscale tarda minutos y `clipUrl`
      // puede estar vencida (las signed URLs de Supabase duran 1h). Con 3h de
      // validez, Replicate alcanza a descargarla. Mismo criterio que traducción
      // y borrado de objetos.
      let videoUrl = clipUrl;
      if (clipStoragePath) {
        try {
          const { data: urlData } = await supabase.storage
            .from('video-project-assets')
            .createSignedUrl(clipStoragePath, 60 * 60 * 3);
          if (urlData?.signedUrl) videoUrl = urlData.signedUrl;
        } catch {
          // Si falla el re-firmado, seguimos con la URL original.
        }
      }

      const result = await upscaleVideo(
        {
          userId: user.id,
          companyId,
          projectId,
          clipId,
          videoUrl,
          durationSec,
          mediaOffsetSec,
          targetResolution: resolution,
        },
        (s) => {
          if (s === 'processing') setProgress('Mejorando calidad…');
          else if (s === 'done') setProgress('Listo, guardando…');
        },
        onIniciado,
      );
      savingRef.current = true;
      await onSave({ url: result.signedUrl, path: result.path, segundos: result.segundos });
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo mejorar el video.');
    } finally {
      setBusy(false);
      savingRef.current = false;
      setProgress(null);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Sin header propio: el título "Mejorar calidad" lo pone el contenedor
          del editor en SU cabecera, junto a la X y al botón de volver. Antes
          estaban los dos —una barra vacía arriba y el título acá abajo—, que
          además dejaba el título lejos del control que lo cierra. */}
      <div className="flex-1 overflow-y-auto p-3 flex flex-col gap-4">
        {/* Preview a tamaño natural → nunca hay barras negras, sea cual sea el
            formato (mismo criterio que EditarVideoIA). */}
        <video
          src={clipUrl}
          className="w-full max-h-[45vh] object-contain rounded-md"
          controls
          playsInline
        />

        {saved ? (
          <div className="text-center py-4">
            <Check className="w-10 h-10 text-brand-primary mx-auto mb-2" />
            <p className="text-[14px] font-semibold text-brand-black">
              ¡Calidad mejorada!
            </p>
            <p className="text-[13px] text-brand-black/70 mt-1">
              El clip del timeline se reemplazó por la versión {resLabel}.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-4 px-4 py-2 rounded-md bg-neutral-100 text-[13px] font-medium hover:bg-neutral-200"
            >
              Cerrar
            </button>
          </div>
        ) : (
          <>
            {/* Selector de resolución destino */}
            <div>
              <p className="text-[13px] font-medium text-brand-black mb-1.5">
                Resolución destino
              </p>
              <div className="flex gap-2">
                {RESOLUTIONS.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    disabled={busy}
                    onClick={() => setResolution(r.id)}
                    className={`flex-1 py-2 rounded-md text-[14px] font-semibold border transition-colors ${
                      r.id === resolution
                        ? 'bg-brand-primary text-white border-brand-primary'
                        : 'bg-white text-brand-black border-neutral-300 hover:bg-neutral-50'
                    } disabled:opacity-50`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
              {resolution === '4k' && (
                <p className="text-[12px] text-brand-black/60 mt-1.5">
                  El 4K da la mejor calidad, pero cuesta más por segundo y tarda
                  más.
                </p>
              )}
            </div>

            {error && (
              <div className="px-3 py-2 rounded-md bg-red-50 border border-red-200">
                <p className="text-[13px] text-red-700 flex items-start gap-1.5">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{error}</span>
                </p>
              </div>
            )}

            <button
              type="button"
              onClick={handleUpscale}
              disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-md text-[14.5px] font-semibold bg-brand-primary text-white hover:bg-brand-primary-hover disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {busy ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {progress || 'Mejorando…'}
                </>
              ) : (
                <>
                  Mejorar a {resLabel}
                  <CreditBadge credits={cost} tone="on-primary" />
                </>
              )}
            </button>

            <p className="text-[12px] text-brand-black/50">
              Tarda varios minutos. Podés seguir editando mientras tanto; si
              cerrás el editor, sigue y se aplica cuando vuelvas.
            </p>
          </>
        )}
      </div>
    </div>
  );
};
